import React, { useState, useRef } from 'react';
import { Camera } from 'lucide-react';

const InvoiceScanner = ({ onScanComplete }) => {
  const [image, setImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Fields to extract from invoice
  const [fieldSettings, setFieldSettings] = useState({
    invoiceNumber: { enabled: true, label: 'מספר חשבונית' },
    invoiceDate: { enabled: true, label: 'תאריך חשבונית' },
    vendorName: { enabled: true, label: 'שם ספק' },
    description: { enabled: true, label: 'תיאור/פירוט' },
    amount: { enabled: true, label: 'סכום' }
  });

  // Start the camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setShowCamera(true);
      }
    } catch (err) {
      alert("לא ניתן לגשת למצלמה. אנא בדוק את הרשאות המצלמה ונסה שוב.");
    }
  };

  // Stop the camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const imageDataUrl = canvas.toDataURL('image/jpeg');
      setImage(imageDataUrl);
      
      // Stop camera after taking photo
      stopCamera();
      
      // Process the captured image
      processImage(imageDataUrl);
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        setImage(event.target.result);
        processImage(event.target.result);
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Process the image (mock OCR for now)
  const processImage = (imageData) => {
    setIsProcessing(true);
    
    // In a real implementation, you would send the image to an OCR service
    // For demo purposes, we'll simulate OCR processing with a timeout
    setTimeout(() => {
      // Mock extraction results
      const mockResults = {
        invoiceNumber: "INV-2025-0156",
        invoiceDate: "2025-05-14",
        vendorName: "חברת ניקיון הקריה",
        description: "ניקיון שבועי לבניין",
        amount: "750"
      };
      
      setExtractedData(mockResults);
      setIsProcessing(false);
    }, 1500);
    
    // In a real implementation, you would do something like:
    /*
    const sendToOCR = async () => {
      try {
        const response = await fetch('your-ocr-api-endpoint', {
          method: 'POST',
          body: JSON.stringify({ image: imageData }),
          headers: { 'Content-Type': 'application/json' }
        });
        
        const data = await response.json();
        setExtractedData(data);
      } catch (error) {
        alert('אירעה שגיאה בעיבוד התמונה. אנא נסה שוב.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    sendToOCR();
    */
  };

  // Update field settings
  const updateFieldSetting = (field, enabled) => {
    setFieldSettings(prev => ({
      ...prev,
      [field]: { ...prev[field], enabled }
    }));
  };

  // Confirm extracted data and send to parent component
  const confirmExtractedData = () => {
    // Create an object with only the enabled fields
    const filteredData = {};
    
    Object.entries(fieldSettings).forEach(([field, { enabled }]) => {
      if (enabled && extractedData[field]) {
        filteredData[field] = extractedData[field];
      }
    });
    
    // Map to the expected format for expense creation
    const expenseData = {
      date: filteredData.invoiceDate || '',
      description: `${filteredData.description || ''} ${filteredData.invoiceNumber ? `(חשבונית ${filteredData.invoiceNumber})` : ''}`,
      amount: filteredData.amount || '',
      category: 'אחר', // Default category, can be changed by user later
      vendor: filteredData.vendorName || '' // Additional field
    };
    
    // Send the data to parent component
    onScanComplete(expenseData);
    
    // Reset the scanner
    resetScanner();
  };

  // Reset the scanner
  const resetScanner = () => {
    setImage(null);
    setExtractedData(null);
    stopCamera();
  };

  return (
    <div className="invoice-scanner">
      <h2>סריקת חשבונית</h2>
      
      {!image && !showCamera && (
        <div className="scanner-options">
          <button 
            className="camera-button"
            onClick={startCamera}
          >
            <Camera size={24} />
            צלם חשבונית
          </button>
          <div className="file-upload">
            <label htmlFor="invoice-file" className="upload-button">
              העלה תמונת חשבונית
            </label>
            <input
              id="invoice-file"
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
          </div>
        </div>
      )}
      
      {showCamera && (
        <div className="camera-container">
          <video 
            ref={videoRef}
            autoPlay 
            playsInline
            className="camera-preview"
          />
          <div className="camera-controls">
            <button onClick={capturePhoto} className="capture-button">
              צלם
            </button>
            <button onClick={stopCamera} className="cancel-button">
              ביטול
            </button>
          </div>
        </div>
      )}
      
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      {image && (
        <div className="preview-container">
          <img src={image} alt="תמונת חשבונית" className="invoice-preview" />
          
          {isProcessing && (
            <div className="processing-overlay">
              <div className="spinner"></div>
              <p>מעבד חשבונית...</p>
            </div>
          )}
        </div>
      )}
      
      {extractedData && !isProcessing && (
        <div className="extracted-data">
          <h3>נתונים שזוהו</h3>
          
          <div className="field-settings">
            {Object.entries(fieldSettings).map(([field, { enabled, label }]) => (
              <div key={field} className="field-item">
                <div className="field-checkbox">
                  <input
                    type="checkbox"
                    id={`field-${field}`}
                    checked={enabled}
                    onChange={(e) => updateFieldSetting(field, e.target.checked)}
                  />
                  <label htmlFor={`field-${field}`}>{label}</label>
                </div>
                <div className="field-value">
                  {extractedData[field] || 'לא זוהה'}
                </div>
              </div>
            ))}
          </div>
          
          <div className="extracted-actions">
            <button onClick={confirmExtractedData} className="save-button">
              השתמש בנתונים אלו
            </button>
            <button onClick={resetScanner} className="cancel-button">
              ביטול
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceScanner;